import {
    GetRosterCandidateStatusGroupCountsQuery,
    ProcessStatusGroup,
} from '__generated__/graphql'
import { useEffect, useState } from 'react'

type TabCount = {
    current: number
    total: number
}

interface UseTabLabelsParams {
    groupCounts: GetRosterCandidateStatusGroupCountsQuery['getRosterCandidateStatusGroupCounts']
    totalItems: number | undefined
    activeTab: ProcessStatusGroup
}

export function useTabLabels({
    groupCounts,
    totalItems,
    activeTab,
}: UseTabLabelsParams) {
    const [tabCounts, setTabCounts] = useState<
        Record<
            | ProcessStatusGroup.Submitted
            | ProcessStatusGroup.InProcess
            | ProcessStatusGroup.OutOfProcess,
            TabCount
        >
    >({
        [ProcessStatusGroup.Submitted]: { current: 0, total: 0 },
        [ProcessStatusGroup.InProcess]: { current: 0, total: 0 },
        [ProcessStatusGroup.OutOfProcess]: { current: 0, total: 0 },
    })

    useEffect(() => {
        setTabCounts((prev) => ({
            [ProcessStatusGroup.Submitted]: {
                ...prev[ProcessStatusGroup.Submitted],
                total:
                    groupCounts.find(
                        (g) =>
                            g.statusSummaryGroup.summaryGroup ===
                            ProcessStatusGroup.Submitted
                    )?.count ?? 0,
            },
            [ProcessStatusGroup.InProcess]: {
                ...prev[ProcessStatusGroup.InProcess],
                total:
                    groupCounts.find(
                        (g) =>
                            g.statusSummaryGroup.summaryGroup ===
                            ProcessStatusGroup.InProcess
                    )?.count ?? 0,
            },
            [ProcessStatusGroup.OutOfProcess]: {
                ...prev[ProcessStatusGroup.OutOfProcess],
                total:
                    groupCounts.find(
                        (g) =>
                            g.statusSummaryGroup.summaryGroup ===
                            ProcessStatusGroup.OutOfProcess
                    )?.count ?? 0,
            },
        }))
    }, [groupCounts])

    useEffect(() => {
        if (totalItems === undefined) return

        setTabCounts((prev) => ({
            ...prev,
            [activeTab]: {
                ...prev[activeTab],
                current: totalItems,
            },
        }))
    }, [totalItems, activeTab])

    const formatTabLabel = (
        name: string,
        counts: TabCount,
        isActive: boolean
    ): string => {
        if (isActive) {
            return `${name} (${counts.current} of ${counts.total})`
        }
        return `${name} (${counts.total})`
    }

    const getTabLabel = (tab: ProcessStatusGroup): string => {
        const tabNames = {
            [ProcessStatusGroup.Submitted]: 'Submitted',
            [ProcessStatusGroup.InProcess]: 'In Process',
            [ProcessStatusGroup.OutOfProcess]: 'Out of Process',
        }

        const counts = tabCounts[tab]
        const isActive = tab === activeTab
        const name = tabNames[tab]

        return formatTabLabel(name, counts, isActive)
    }

    return { getTabLabel }
}
